/** Saved prompts and user collections, behind a segmented control. */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { CollectionSummary, PromptCard as PromptCardDto } from '@promptvault/shared';
import { fetchFavorites } from '../api/me';
import { toggleFavorite } from '../api/prompts';
import { createCollection, fetchCollectionPrompts, fetchCollections } from '../api/community';
import { BottomSheet } from '../components/BottomSheet';
import { PromptCard } from '../components/PromptCard';
import { Padded, Screen } from '../components/Screen';
import { useToast } from '../components/Toast';
import { ErrorState, Placeholder, SkeletonCells, SkeletonGrid } from '../components/feedback';
import { Button, Cell, SegmentedControl } from '../components/primitives';
import { Media } from '../components/Media';
import { compactNumber } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { haptic } from '../hooks/useTg';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

type Tab = 'saved' | 'collections';

export function Favorites() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { t, lang } = useI18n();

  const [tab, setTab] = useState<Tab>('saved');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');

  const saved = useInfiniteQuery({
    queryKey: ['favorites'],
    queryFn: ({ pageParam }) => fetchFavorites(pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: tab === 'saved',
  });

  const collections = useQuery({
    queryKey: ['collections'],
    queryFn: fetchCollections,
    enabled: tab === 'collections',
  });

  const items = useMemo(() => saved.data?.pages.flatMap((p) => p.items) ?? [], [saved.data]);

  const unfavMut = useMutation({
    mutationFn: (card: PromptCardDto) => toggleFavorite(card.id),
    onMutate: (card) => {
      // Removing from this very list — drop it immediately so the grid doesn't stutter.
      haptic('medium');
      const previous = qc.getQueryData(['favorites']);
      qc.setQueryData(['favorites'], (old: typeof saved.data) =>
        old
          ? {
              ...old,
              pages: old.pages.map((p) => ({
                ...p,
                items: p.items.filter((i) => i.id !== card.id),
              })),
            }
          : old,
      );
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(['favorites'], ctx.previous);
      toast(t.errorTitle, 'error');
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['favorites'] }),
  });

  const createMut = useMutation({
    mutationFn: () => createCollection({ name: name.trim() }),
    onSuccess: () => {
      setCreating(false);
      setName('');
      void qc.invalidateQueries({ queryKey: ['collections'] });
    },
    onError: () => toast(t.errorTitle, 'error'),
  });

  const sentinel = useInfiniteScroll(() => void saved.fetchNextPage(), {
    enabled: Boolean(saved.hasNextPage),
    loading: saved.isFetchingNextPage,
  });

  const openCard = useCallback((card: PromptCardDto) => navigate(`/prompt/${card.id}`), [navigate]);

  return (
    <Screen root chrome="surface">
      <div className="hairline-b sticky top-0 z-10 bg-surface px-gutter pb-3 pt-2.5">
        <SegmentedControl<Tab>
          value={tab}
          onChange={setTab}
          options={[
            { value: 'saved', label: t.savedTab },
            { value: 'collections', label: t.collectionsTab },
          ]}
        />
      </div>

      {tab === 'saved' ? (
        saved.isLoading ? (
          <Padded className="pt-3.5">
            <SkeletonGrid count={4} />
          </Padded>
        ) : saved.isError ? (
          <ErrorState
            message={(saved.error as Error)?.message}
            onRetry={() => void saved.refetch()}
          />
        ) : items.length === 0 ? (
          <Placeholder
            icon="heart"
            title={t.favEmptyTitle}
            description={t.favEmptySub}
            action={{ label: t.browsePrompts, onClick: () => navigate('/') }}
          />
        ) : (
          <Padded className="pt-3.5">
            <div className="grid grid-cols-2 gap-2">
              {items.map((card, i) => (
                <PromptCard
                  key={card.id}
                  card={card}
                  onOpen={openCard}
                  onToggleFavorite={(c) => unfavMut.mutate(c)}
                  favorited
                  priority={i < 4}
                />
              ))}
            </div>
            <div ref={sentinel} aria-hidden="true" className="h-1" />
          </Padded>
        )
      ) : collections.isLoading ? (
        <Padded className="pt-3.5">
          <SkeletonCells count={3} />
        </Padded>
      ) : collections.isError ? (
        <ErrorState
          message={(collections.error as Error)?.message}
          onRetry={() => void collections.refetch()}
        />
      ) : (
        <Padded className="flex flex-col gap-2 pt-3.5">
          {(collections.data ?? []).length === 0 ? (
            <Placeholder
              icon="plus"
              title={t.collectionsEmptyTitle}
              description={t.collectionsEmptySub}
              action={{ label: t.newCollection, onClick: () => setCreating(true) }}
            />
          ) : (
            <>
              {collections.data!.map((c) => (
                <CollectionRow
                  key={c.id}
                  collection={c}
                  meta={t.promptsCount(compactNumber(c.itemCount, lang))}
                  onOpen={() => navigate(`/collection/${c.id}`)}
                />
              ))}
              <Button
                variant="dashed"
                className="w-full"
                icon="plus"
                onClick={() => setCreating(true)}
              >
                {t.newCollection}
              </Button>
            </>
          )}
        </Padded>
      )}

      <div className="h-6" />

      <BottomSheet
        open={creating}
        onClose={() => setCreating(false)}
        title={t.newCollection}
        footer={
          <Button
            size="lg"
            className="w-full"
            disabled={name.trim().length === 0 || createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            {t.done}
          </Button>
        }
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t.namePrompt}
          maxLength={80}
          autoFocus
          className="w-full rounded-card bg-fill px-3.5 py-3 text-body text-text outline-none placeholder:text-hint"
        />
      </BottomSheet>
    </Screen>
  );
}

/** Collection row with the design's overlapping cover stack. */
function CollectionRow({
  collection,
  meta,
  onOpen,
}: {
  collection: CollectionSummary;
  meta: string;
  onOpen: () => void;
}) {
  const covers = collection.covers.length > 0 ? collection.covers : [null, null, null].slice(0, 1);
  return (
    <div className="overflow-hidden rounded-card bg-surface">
      <Cell
        last
        onClick={onOpen}
        chevron
        title={collection.name}
        subtitle={meta}
        before={
          <span className="flex flex-none">
            {covers.map((cover, i) => (
              <span
                key={cover?.id ?? i}
                className="block h-11 w-11 flex-none overflow-hidden rounded-thumb border-2 border-surface"
                style={{ marginInlineStart: i ? -16 : 0 }}
              >
                <Media
                  seed={`${collection.id}-${i}`}
                  example={cover}
                  ratio="1 / 1"
                  variant={i}
                  className="h-full w-full"
                />
              </span>
            ))}
          </span>
        }
      />
    </div>
  );
}

/** One collection's prompts. */
export function CollectionDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useI18n();

  const query = useInfiniteQuery({
    queryKey: ['collection', id],
    queryFn: ({ pageParam }) => fetchCollectionPrompts(id!, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(id),
  });

  const items = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data]);
  const openCard = useCallback((card: PromptCardDto) => navigate(`/prompt/${card.id}`), [navigate]);

  /**
   * The sentinel this screen was missing.
   *
   * `useInfiniteQuery` with a working `getNextPageParam` and nothing to call `fetchNextPage` — so
   * a collection stopped at its first page and the rest was unreachable. The saved tab forty
   * lines above has had one all along, which is why the omission was invisible: the pattern is
   * present in the file, just not on this screen.
   */
  const sentinel = useInfiniteScroll(() => void query.fetchNextPage(), {
    enabled: Boolean(query.hasNextPage),
    loading: query.isFetchingNextPage,
  });

  return (
    <Screen>
      {query.isLoading ? (
        <Padded className="pt-3.5">
          <SkeletonGrid count={4} />
        </Padded>
      ) : query.isError ? (
        <ErrorState
          message={(query.error as Error)?.message}
          onRetry={() => void query.refetch()}
        />
      ) : items.length === 0 ? (
        <Placeholder
          icon="heart"
          title={t.favEmptyTitle}
          description={t.favEmptySub}
          action={{ label: t.browsePrompts, onClick: () => navigate('/') }}
        />
      ) : (
        <Padded className="pt-3.5">
          <div className="grid grid-cols-2 gap-2">
            {items.map((card, i) => (
              <PromptCard key={card.id} card={card} onOpen={openCard} priority={i < 4} />
            ))}
          </div>
          <div ref={sentinel} aria-hidden="true" className="h-1" />
          {query.isFetchingNextPage && (
            <div className="pt-2">
              <SkeletonGrid count={2} />
            </div>
          )}
        </Padded>
      )}
      <div className="h-6" />
    </Screen>
  );
}
